/**
 * PlaceCoreContactSection — Website, Instagram, Phone links + vCard download.
 * Only renders if at least one contact field exists.
 */
import { Globe, Instagram, Phone, UserPlus } from "lucide-react";
import type { PlaceContact } from "@/lib/placecore/public/getPlacePresentation";
import { downloadVCard } from "@/lib/vcard";
import { appendUtmParams } from "@/lib/url/utm";
import { useTranslate } from "@/i18n";

interface Props {
  contact: PlaceContact;
  /** Used for vCard FN field */
  placeName?: string;
  /** Place slug for UTM content param */
  placeSlug?: string;
}

function formatInstagramUrl(value: string): string {
  const handle = value.startsWith("@") ? value.slice(1) : value;
  return `https://instagram.com/${handle}`;
}

function formatInstagramLabel(value: string): string {
  return value.startsWith("@") ? value : `@${value}`;
}

export function PlaceCoreContactSection({ contact, placeName, placeSlug }: Props) {
  const t = useTranslate();
  const hasAny = contact.website || contact.instagram || contact.phone;
  if (!hasAny) return null;

  const canVCard = Boolean(placeName && (contact.phone || contact.website || contact.email));

  return (
    <section>
      <h2 className="font-display text-base font-medium text-foreground mb-3">
        {t("place.contact.heading")}
      </h2>
      <div className="space-y-2.5">
        {contact.website && (
          <a
            href={appendUtmParams(contact.website, {
              campaign: 'place_page',
              content: placeSlug,
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 text-sm text-foreground/80 hover:text-primary transition-colors"
          >
            <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{contact.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}</span>
          </a>
        )}
        {contact.instagram && (
          <a
            href={formatInstagramUrl(contact.instagram)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 text-sm text-foreground/80 hover:text-primary transition-colors"
          >
            <Instagram className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>{formatInstagramLabel(contact.instagram)}</span>
          </a>
        )}
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            className="flex items-center gap-2.5 text-sm text-foreground/80 hover:text-primary transition-colors"
          >
            <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>{contact.phone}</span>
          </a>
        )}
        {canVCard && (
          <button
            type="button"
            onClick={() => downloadVCard(placeName!, {
              phone: contact.phone,
              email: contact.email,
              website: contact.website,
            })}
            className="flex items-center gap-2.5 text-sm text-foreground/80 hover:text-primary transition-colors"
          >
            <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
            <span>{t("place.contact.addContact")}</span>
          </button>
        )}
      </div>
    </section>
  );
}
